import { useEffect, useState } from 'react';
import { CallStatus, useCallable } from '../../lib/useCallable';

/**
 * How much the conflict check request page is used.
 *
 * Two numbers everywhere, because "how many have been submitted" means different things to
 * different people: REQUESTS is how often the page is used, PARTIES (the names or entities on
 * those requests) is how much work reached the Conflicts team. A request with thirty names is
 * one of the first and thirty of the second.
 *
 * The month history is a bar list rather than a chart with a tooltip: twelve rows with the
 * number beside each bar is the table and the picture at once, and nothing has to be hovered to
 * be read. Bars are the requests; the parties figure sits beside it in muted text.
 *
 * Read through a callable: conflictChecks is denied to every browser, and a count is the only
 * part of a request an admin needs.
 */

const number = new Intl.NumberFormat('en-US');

function Tile({ label, value, detail }) {
  return (
    <div className="rounded border border-slate-200 px-3 py-2 dark:border-slate-700">
      <dt className="text-xs text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="text-xl font-semibold text-ink dark:text-slate-100">{value}</dd>
      {detail && <dd className="text-xs text-slate-500 dark:text-slate-400">{detail}</dd>}
    </div>
  );
}

export default function ConflictStats() {
  const { run, busy, error } = useCallable();
  const [stats, setStats] = useState(null);

  const load = async () => {
    const result = await run('adminConflictStats', {});
    if (result) setStats(result);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const maxRequests = stats ? Math.max(1, ...stats.months.map((m) => m.requests)) : 1;
  const lastSubmitted = stats && stats.lastSubmittedAt ? new Date(stats.lastSubmittedAt) : null;

  return (
    <section className="rounded-lg bg-white p-4 shadow dark:bg-slate-900">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Requests submitted</h3>
          <CallStatus busy={busy} error={error} saved={false} />
        </div>
        <button
          type="button"
          onClick={load}
          disabled={busy}
          className="text-xs text-firm-700 hover:underline disabled:opacity-50 dark:text-firm-100"
        >
          Refresh
        </button>
      </div>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        How much the conflict check request page is used. Requests are submissions of the page;
        parties are the names or entities on them, which is the number of lookups the Conflicts
        team receives.
      </p>

      {stats && (
        <>
          <dl className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            <Tile label="Last 7 days" value={number.format(stats.last7Days)} detail="requests" />
            <Tile label="Last 30 days" value={number.format(stats.last30Days)} detail="requests" />
            <Tile label="This year" value={number.format(stats.thisYear)} detail="requests" />
            <Tile
              label="All time"
              value={number.format(stats.total)}
              detail={`requests · ${number.format(stats.parties)} parties`}
            />
            <Tile label="People who have submitted" value={number.format(stats.submitters)} />
            <Tile
              label="Most recent"
              value={lastSubmitted ? lastSubmitted.toLocaleDateString() : '—'}
              detail={lastSubmitted ? lastSubmitted.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : 'nothing yet'}
            />
          </dl>

          <h4 className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Last twelve months
          </h4>
          <table className="mt-1 w-full text-sm">
            <thead className="sr-only">
              <tr>
                <th scope="col">Month</th>
                <th scope="col">Requests</th>
                <th scope="col">Parties</th>
              </tr>
            </thead>
            <tbody>
              {stats.months.map((month) => (
                <tr key={month.key}>
                  <th scope="row" className="w-20 py-0.5 pr-2 text-left text-xs font-normal text-slate-600 dark:text-slate-300">
                    {month.label}
                  </th>
                  <td className="py-0.5">
                    <div className="flex items-center gap-2">
                      <div
                        className="h-3 rounded-r-sm bg-firm-700 dark:bg-firm-400"
                        style={{ width: `${(month.requests / maxRequests) * 100}%`, minWidth: month.requests ? '4px' : 0 }}
                        aria-hidden="true"
                      />
                      <span className="tabular-nums text-ink dark:text-slate-100">{number.format(month.requests)}</span>
                    </div>
                  </td>
                  <td className="w-24 py-0.5 text-right text-xs tabular-nums text-slate-500 dark:text-slate-400">
                    {month.parties ? `${number.format(month.parties)} parties` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}
